// BOQ tab — bill of quantities with all-inclusive unit rates (amounts sum to the bid total).
import { ListOrdered } from "lucide-react";
import NoDataState from "modules/swiftboard/components/NoDataState";
import TabSkeleton from "./TabSkeleton";
import { useWorkspace } from "../../../hooks/useEstimateWorkspace";
import { Card, CardHeader, TD, TD_NUM, TH } from "../../../components/ui";
import { fmtCurrency, fmtQty } from "../../../utils/format";

export default function BoqTab() {
  const { calc } = useWorkspace();
  if (!calc) return <TabSkeleton />;

  const rows = calc.boq;
  const sumAmount = rows.reduce((a, r) => a + r.amount, 0);

  return (
    <Card>
      <CardHeader
        icon={ListOrdered}
        title="Bill of Quantities"
        subtitle="Rates are all-inclusive (labor, material, equipment, GC, overhead, profit) — grand total equals the Bid total"
        actions={<span className="text-[12.5px] font-semibold tabular-nums text-text_primary">{fmtCurrency(calc.waterfall.total)}</span>}
      />
      {rows.length === 0 ? (
        <NoDataState icon="file" title="No priced lines" message="Add or fix takeoff lines to build the BOQ." showBack={false} showRefresh={false} size="small" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse" aria-label="Bill of quantities">
            <thead>
              <tr>
                <th className={TH}>Item</th>
                <th className={TH}>Description</th>
                <th className={TH}>Unit</th>
                <th className={`${TH} text-right`}>Qty</th>
                <th className={`${TH} text-right`}>Rate / Unit</th>
                <th className={`${TH} text-right`}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <td className={`${TD} font-mono text-[12px] text-text_secondary`}>{r.item}</td>
                  <td className={`${TD} whitespace-normal min-w-[320px]`}>
                    <div>{r.description}</div>
                    <div className="text-[11px] text-text_muted">
                      {r.lineCount} takeoff line{r.lineCount === 1 ? "" : "s"}
                    </div>
                  </td>
                  <td className={TD}>{r.unit}</td>
                  <td className={TD_NUM}>{fmtQty(r.qty)}</td>
                  <td className={TD_NUM}>{fmtCurrency(r.rate)}</td>
                  <td className={`${TD_NUM} font-medium`}>{fmtCurrency(r.amount)}</td>
                </tr>
              ))}
              <tr className="bg-surface_alt">
                <td className={`${TD} font-semibold`} colSpan={5}>
                  Grand Total <span className="ml-1 text-[11px] font-normal text-text_muted">(equals Bid total)</span>
                </td>
                <td className={`${TD_NUM} font-semibold`}>{fmtCurrency(sumAmount)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
